import get from 'lodash/get';
import DialogueType from '../../../../src/components/questions/DialogueType';
import { getNpcCharacters } from '~lib/common/utils/characters';
import isTagOnlyText from '~lib/common/utils/isTagOnlyText';
import { dialogueMsg } from '../validationMessages';

const MAX_WORD_LENGTH = 45;

export const TAG_ONLY_ANSWER_MESSAGE = 'Emotion and reaction tags must accompany speech, e.g., "[quietly] I\'m not feeling good."';

function isDialogueTooLong(sentence) {
  const words = sentence.split(' ');
  for (const word of words) {
    if (word.length > MAX_WORD_LENGTH) {
      return true;
    }
  }
  return false;
}

function validateDialogue(caseConf) {
  const warnings = [];
  const errors = [];
  const npcs = getNpcCharacters(caseConf) || [];

  function validatePrompt(dialogue, label, characterKey = 'PATIENT') {
    const isPatient = (characterKey === 'PATIENT')
    const promptKey = isPatient ? 'patientPrompt.content.en' : 'prompt.content.en'
    const promptContent = get(dialogue, promptKey);
    const componentId = isPatient ? DialogueType.AI_PATIENT_PROMPT : DialogueType.AI_NPC_PROMPT;
    const dialogueType = componentId;
    const metadata = { characterKey, dialogueType }
    if (!promptContent) {
      errors.push(
        dialogueMsg(
          `${label} prompt cannot be empty`,
          componentId,
          metadata
        ));
    }
    else if (!isPatient) {
      // Check NPC prompts are not identical
      for (const npc of npcs) {
        if (npc.key !== characterKey) {
          const otherNpcPromptContent = get(npc.dialogue, 'prompt.content.en');
          if (promptContent === otherNpcPromptContent) {
            errors.push(
              dialogueMsg(
                `${label} prompt cannot be equal to ${npc.label} prompt`,
                componentId,
                metadata
              ));
          }
        }
      }
    }
  }

  // Only a custom answer is sent to generate-audio; an AI response is written at runtime
  function validateTagOnlyAnswer(item, metadata) {
    if (get(item, 'options.response') === 'AI') return;
    if (isTagOnlyText(get(item, 'options.answer.en'))) {
      errors.push(
        dialogueMsg(
          TAG_ONLY_ANSWER_MESSAGE,
          item.id,
          {
            errorId: 'tagOnlyAnswer',
            ...metadata
          }));
    }
  }

  function validateLegacyQuestions(questions, characterKey = 'PATIENT') {
    if (!questions) return;
    const isPatient = (characterKey === 'PATIENT');
    const dialogueType = isPatient ? DialogueType.Q_AND_A : DialogueType.NPC_Q_AND_A;
    const metadata = { characterKey, dialogueType }
    for (const question of questions) {
      if (!question.content.en) {
        errors.push(dialogueMsg('Empty question', question.id, { errorId: 'missingQuestion', ...metadata }));
      } else if (isDialogueTooLong(question.content.en)) {
        errors.push(dialogueMsg(`Question has word that exceeds ${MAX_WORD_LENGTH} characters in length`, question.id, { errorId: 'wordTooLong', ...metadata }));
      }
      validateTagOnlyAnswer(question, metadata);
    }
  }

  function validateLegacyCommands(commands, characterKey = 'PATIENT') {
    if (!commands) return;
    const isPatient = (characterKey === 'PATIENT');
    const dialogueType = isPatient ? DialogueType.COMMANDS : DialogueType.NPC_COMMANDS;
    const metadata = { characterKey, dialogueType }
    for (const command of commands) {
      if (!command.content.en) {
        warnings.push(dialogueMsg('Empty command', command.id, { warningId: 'missingQuestion', ...metadata }));
      } else if (isDialogueTooLong(command.content.en)) {
        errors.push(dialogueMsg(`Command has word that exceeds ${MAX_WORD_LENGTH} characters in length`, command.id, { errorId: 'wordTooLong', ...metadata }));
      }
    }
  }

  function validateLegacyUnsolicited(unsolicited, characterKey = 'PATIENT') {
    if (!unsolicited) return;
    const isPatient = (characterKey === 'PATIENT');
    const dialogueType = isPatient ? DialogueType.UNSOLICITED : DialogueType.NPC_UNSOLICITED_DIALOGUE;
    const metadata = { characterKey, dialogueType }
    for (const item of unsolicited) {
      validateTagOnlyAnswer(item, metadata);
    }
  }

  function validateAIQuestions(aiQuestions, characterKey = 'PATIENT') {
    if (!aiQuestions) return;
    const isPatient = (characterKey === 'PATIENT');
    const dialogueType = isPatient ? DialogueType.AI_DIRECTED_DIALOGUE : DialogueType.AI_NPC_DIALOGUE;
    const metadata = { characterKey, dialogueType }
    for (const question of aiQuestions) {
      if (!question.content.en) {
        errors.push(
          dialogueMsg(
            'Empty question',
            question.id,
            {
              errorId: 'missingQuestion',
              ...metadata
            }
          )
        );
      } else if (isDialogueTooLong(question.content.en)) {
        errors.push(
          dialogueMsg(
            `Question has word that exceeds ${MAX_WORD_LENGTH} characters in length`,
            question.id,
            {
              errorId: 'wordTooLong',
              ...metadata
            }));
      }
      if (!question.options.reason || !question.options.reason.en) {
        errors.push(
          dialogueMsg(
            'Provide AI with reason for response',
            question.id,
            {
              errorId: 'missingReason',
              ...metadata
            }));
      }
      validateTagOnlyAnswer(question, metadata);
    }
  }

  function validateAiUnsolicited(aiUnsolicited, characterKey = 'PATIENT') {
    if (!aiUnsolicited) return;
    const isPatient = (characterKey === 'PATIENT');
    const dialogueType = isPatient ? DialogueType.AI_DIRECTED_DIALOGUE : DialogueType.AI_NPC_DIALOGUE;
    const metadata = { characterKey, dialogueType }
    for (const item of aiUnsolicited) {
      if (!item.options.reason || !item.options.reason.en) {
        errors.push(
          dialogueMsg(
            'Provide AI with reason for response.',
            item.id,
            {
              errorId: 'missingReason',
              ...metadata
            }))
      }
      validateTagOnlyAnswer(item, metadata);
    }
  }

  if (caseConf.dialogue.aiEnabled) {
    // Patient Prompt
    validatePrompt(caseConf.dialogue, 'Patient');
    // Patient AI Questions
    const patientAiQuestions = caseConf.dialogue.aiQuestions || [];
    validateAIQuestions(patientAiQuestions);
    // Patient AI Unsolicited
    const patientAiUnsolicited = caseConf.dialogue.aiUnsolicited || [];
    validateAiUnsolicited(patientAiUnsolicited);

    // NPCs
    for (const npc of npcs) {
      if (!npc.dialogue) continue;
      validatePrompt(npc.dialogue, npc.label, npc.key);
      const npcAiQuestions = npc.dialogue.aiQuestions || [];
      const npcAiUnsolicited = npc.dialogue.aiUnsolicited
      validateAIQuestions(npcAiQuestions, npc.key);
      validateAiUnsolicited(npcAiUnsolicited, npc.key);
    }
    
  } else {
    // Validate Patient
    const questions = caseConf.dialogue
    .questions || [];
    validateLegacyQuestions(questions);

    const commands = caseConf.dialogue.commands || [];
    validateLegacyCommands(commands);

    const unsolicited = caseConf.dialogue.unsolicited || [];
    validateLegacyUnsolicited(unsolicited);

    for (const npc of npcs) {
      if (!npc.dialogue) continue;
      validateLegacyQuestions(npc.dialogue.questions, npc.key);
      validateLegacyCommands(npc.dialogue.commands, npc.key)
      validateLegacyUnsolicited(npc.dialogue.unsolicited, npc.key);
    }
  }

  const calls = caseConf.dialogue.calls || [];
  for (const call of calls) {
    // Go To navigates by characterKey; a callee's tab is keyed by its call id
    const itemMetadata = { characterKey: call.id, dialogueType: DialogueType.Q_AND_A };
    const unsolicitedMetadata = { characterKey: call.id, dialogueType: DialogueType.UNSOLICITED };
    for (const item of call.items) {
      if (!item.content.en) {
        warnings.push(dialogueMsg('Empty call question', item.id, { warningId: 'missingQuestion', ...itemMetadata }))
      } else if (isDialogueTooLong(item.content.en)) {
        errors.push(dialogueMsg(`Call question has word that exceeds ${MAX_WORD_LENGTH} characters in length`, item.id, { errorId: 'wordTooLong', ...itemMetadata }));
      }
      validateTagOnlyAnswer(item, itemMetadata);
    }
    for (const item of call.unsolicited || []) {
      validateTagOnlyAnswer(item, unsolicitedMetadata);
    }
  }

  return {
    errors,
    warnings,
  };
}

export default validateDialogue;
